import i18next from 'i18next'
import { ChartDataset, ChartOptions, TooltipItem } from 'chart.js'
import { formatAmount } from 'utils/format'
import { createChartDataArray, formatChartDateLabel } from 'utils/chartjs'
import { CHART_COLORS, getBarChartDefaults } from 'components/charts/constants'
import { ChartLabels } from 'components/charts/types'
import { externalTooltipHandler } from 'components/charts/chart-tooltip/tooltip.ts'
import { ExternalTooltipContext } from 'components/charts/types.ts'
import { CycleViews } from 'pages/app/billing/billing-cycles/types'
import { getChartPattern, getSettlementChartColor } from '../utils'
import { Granularity } from 'types/contracts.ts'
import { TOOLTIP_CONFIG } from 'components/charts/chart-tooltip/config.ts'
import { BillingCycleChartOptions } from '../types.ts'
import { DSChartColors } from 'types/colors.ts'
import { BillShock } from '@boltenergy-be/shared'

export const getBillshockChartOptions = ({
  labels,
  minMax,
  isMobile,
  handleHoverDataPoint
}: BillingCycleChartOptions): ChartOptions<'bar'> => {
  const defaults = getBarChartDefaults({ isMobile })
  const {
    scales: { y, x }
  } = defaults

  return {
    ...defaults,
    maintainAspectRatio: false,
    skipNull: true,
    scales: {
      y: {
        ...y,
        suggestedMin: minMax?.minDataValue,
        suggestedMax: minMax?.maxDataValue,
        ticks: {
          ...y.ticks,
          callback: (value: string | number) => formatAmount(Number(value), { fractionDigits: 0 })
        }
      },
      x: {
        ...x,
        ticks: {
          font: x.ticks.font,
          callback: (value: string | number) => {
            const index = Number(value)
            if (index === labels.length - 1) return labels[index]
            return formatChartDateLabel(labels[index], Granularity.MONTH)
          },
          color: x.ticks.color
        }
      }
    },
    plugins: {
      yScaleTitle: {
        labels: {
          y: '€'
        }
      },
      annotation: {
        common: {
          drawTime: 'beforeDraw'
        },
        annotations: {
          lastColumn: {
            type: 'box',
            backgroundColor: CHART_COLORS.backgroundHighlight,
            borderWidth: 0,
            xMax: 0.5 + labels.length - 1,
            xMin: -0.5 + labels.length - 1
          }
        }
      },
      tooltip: {
        // Disable the on-canvas tooltip
        ...TOOLTIP_CONFIG,
        external: (context: ExternalTooltipContext<'bar'>) => {
          const { tooltip } = context
          const currentDataPoint = tooltip?.dataPoints?.[0]?.dataIndex
          const hideTooltip = tooltip.opacity === 0
          handleHoverDataPoint(hideTooltip ? null : currentDataPoint)
          return externalTooltipHandler({
            context,
            totalColumns: labels.length
          })
        },
        callbacks: {
          title: (context: TooltipItem<'bar'>[]) =>
            context?.[0]?.dataIndex === labels.length - 1
              ? i18next.t(($) => $.billingCycle.chart.settlement, {
                  ns: 'billing'
                })
              : formatChartDateLabel(context?.[0]?.label, Granularity.MONTH, true)
        }
      }
    }
  }
}

export const getBillingCycleChartDatasets = (
  labels: ChartLabels,
  data: BillShock,
  activePeriod: CycleViews,
  isMobile?: boolean,
  adjustedBillShockAmount?: number
): ChartDataset<'bar', (number | null)[]>[] => {
  const settlementColor = getSettlementChartColor(activePeriod === CycleViews.CURRENT ? data.settlement.billShockAmount : 0)
  const maxBarThickness = 94
  const borderRadius = isMobile ? 2 : 4

  return [
    {
      label: i18next.t(($) => $.billingCycle.chart.tooltips.billedInstalment, {
        ns: 'billing'
      }),
      backgroundColor: CHART_COLORS.pink,
      data: createChartDataArray(
        labels.length,
        data.past.map((instalment) => instalment.billedInstalment)
      ),
      maxBarThickness,
      borderRadius
    },
    {
      label: i18next.t(
        ($) => $.billingCycle.chart.tooltips[activePeriod === CycleViews.CURRENT ? 'plannedInstalment' : 'proposedInstalment'],
        { ns: 'billing' }
      ),
      backgroundColor: getChartPattern(activePeriod === CycleViews.CURRENT ? DSChartColors.PINK : DSChartColors.LIGHTBLUE),
      data: createChartDataArray(
        labels.length,
        data.future.map((instalment) =>
          activePeriod === CycleViews.CURRENT ? instalment.plannedInstalment : instalment.proposedInstalment
        ),
        data.past.length
      ),
      maxBarThickness,
      borderRadius
    },
    {
      label: i18next.t(($) => $.billingCycle.chart.tooltips.billShockAmount, {
        ns: 'billing'
      }),
      backgroundColor: settlementColor,
      data: createChartDataArray(
        labels.length,
        [
          activePeriod === CycleViews.PROPOSED ? (adjustedBillShockAmount ? adjustedBillShockAmount : -10) : data.settlement.billShockAmount
        ],
        labels.length - 1
      ),
      maxBarThickness,
      borderRadius
    }
  ]
}
